/**
 * AdminMokaQueue.jsx — Moka tahsilat eşleştirme kuyruğu.
 * Route: /admin/moka-queue · Roles: admin, superadmin (bkz. App.js RoleRoute;
 * danışman menüsünde bu sayfaya link YOK — bkz. lib/adminNav.js DANISMAN_NAV).
 *
 * GET /admin/moka/queue parametresiz çağrıldığında backend varsayılan olarak
 * yalnızca insan onayı BEKLEYEN kayıtları döner (`queued` + `unmatched_link_not_found`
 * — bkz. routes/moka_links.py `_QUEUE_STATUSES`). Diğer durumlar (onaylanan/reddedilen/
 * otomatik tahsil edilen/başarısız) filtre menüsünden ayrıca görüntülenebilir —
 * salt geçmiş kaydı, kart bu durumlarda aksiyon göstermez (bkz. MokaQueueCard
 * `actionable`).
 */
import { useState } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { PageHeader, EmptyState, LoadingState, Pagination } from '../../components/admin';
import MokaQueueCard from '../../components/admin/moka/MokaQueueCard';
import { Button } from '../../components/ui/button';
import { useLanguage } from '../../contexts/LanguageContext';
import { useMokaQueue } from '../../api/mutations/moka';
import { useAccountingBanks } from '../../api/queries/accounting';
import { AlertTriangle, Inbox, RefreshCw } from 'lucide-react';

const LIMIT = 20;

const STATUS_FILTERS = [
  { value: '', labelKey: 'admin.moka_queue_filter_pending' },
  { value: 'queued', labelKey: 'admin.moka_queue_filter_queued' },
  { value: 'unmatched_link_not_found', labelKey: 'admin.moka_queue_filter_unmatched' },
  { value: 'approved', labelKey: 'admin.moka_queue_filter_approved' },
  { value: 'rejected', labelKey: 'admin.moka_queue_filter_rejected' },
  { value: 'auto_collected', labelKey: 'admin.moka_queue_filter_auto' },
  { value: 'ignored_not_successful', labelKey: 'admin.moka_queue_filter_ignored' },
];

export default function AdminMokaQueue() {
  const { t, lang } = useLanguage();
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);

  const skip = (page - 1) * LIMIT;
  const { data, isLoading, isError, isFetching, refetch } = useMokaQueue({ matchStatus: statusFilter || undefined, skip, limit: LIMIT });
  const { data: banks = [] } = useAccountingBanks();
  const posBanks = banks.filter((b) => (b.account_type || 'bank') === 'pos' && b.is_active !== false);

  const items = data?.queue || [];
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <AdminLayout>
      <PageHeader
        breadcrumbs={[
          { label: t('admin.nav.finance'), href: '/admin/accounting' },
          { label: t('admin.nav.mokaQueue') },
        ]}
        title={t('admin.moka_queue_title')}
        subtitle={t('admin.moka_queue_subtitle')}
      />

      <div className="mb-4">
        <label htmlFor="moka-queue-status-filter" className="text-xs font-bold block mb-1">
          {t('admin.moka_queue_filter_label')}
        </label>
        <select
          id="moka-queue-status-filter"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          data-testid="moka-queue-status-filter"
          className="px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white font-semibold text-sm w-full sm:w-64"
        >
          {STATUS_FILTERS.map((f) => (
            <option key={f.value || 'pending'} value={f.value}>{t(f.labelKey)}</option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <LoadingState variant="cards" count={4} />
      ) : isError ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-destructive/40 bg-destructive/5 p-4">
          <div className="flex items-center gap-2 text-sm text-destructive">
            <AlertTriangle size={18} className="shrink-0" aria-hidden="true" />
            <div>
              <p className="font-semibold">{t('admin.moka_queue_error_title')}</p>
              <p>{t('admin.moka_queue_error_desc')}</p>
            </div>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw size={14} className="mr-1" aria-hidden="true" />
            {t('admin.moka_queue_error_retry')}
          </Button>
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<Inbox size={40} strokeWidth={1.5} />}
          title={t('admin.moka_queue_empty_title')}
          description={t('admin.moka_queue_empty_desc')}
        />
      ) : (
        <div className="flex flex-col gap-3" data-testid="moka-queue-list">
          {items.map((item) => (
            <MokaQueueCard key={item.dealer_payment_id} item={item} posBanks={posBanks} t={t} lang={lang} />
          ))}
        </div>
      )}

      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        showInfo
        from={total === 0 ? 0 : skip + 1}
        to={Math.min(skip + LIMIT, total)}
        total={total}
        loading={isLoading}
      />
    </AdminLayout>
  );
}
